import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import type {
  SnackbarFactory,
  SnackbarRetryWrapperFactory,
} from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { XeroAccount } from '@/features/bookkeeping/platform-connections/BookkeepingPlatformConnection'
import type { BookkeepingPlatformConnectionStore } from '@/features/bookkeeping/platform-connections/BookkeepingPlatformConnectionStore'
import type { BookkeepingConnectionTarget } from '@/features/bookkeeping/components/BookkeepingPlatformSelectionDialog/BookkeepingPlatformSelectionDialogViewModel'

/**
 * The ViewModal options.
 */
export interface XeroConnectionsDialogViewModelOptions {
  /**
   * The bookkeeping connection target.
   */
  target: BookkeepingConnectionTarget

  /**
   * The function to call when the user provides a connection.
   */
  onAction: (quickBooksAccount: XeroAccount) => Promise<unknown>
}

export class XeroConnectionsDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * The scroll provider.
   */
  scrollProvider = new InfiniteScrollProvider({
    limit: 10,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createSnackbarRetryWrapper(
        'Unable to load Xero accounts',
        signal,
      )

      return withRetry(() =>
        this._store.listXeroPlatformConnections(paging, signal),
      )
    },
  })

  constructor(
    private readonly _options: XeroConnectionsDialogViewModelOptions,
    private readonly _store: BookkeepingPlatformConnectionStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _newSnackbar: SnackbarFactory,
  ) {}

  /**
   * The list of {@link XeroAccount}.
   */
  @computed
  get accounts() {
    return this._store.xeroAccounts
  }

  /**
   * Whether the account list is empty.
   */
  @computed
  get isEmpty() {
    return this.scrollProvider.items.length === 0
  }

  /** @inheritdoc */
  activate() {
    this._lifecycle.activate()

    void this.scrollProvider.fetch({ signal: this._lifecycle.signal })
  }

  /** @inheritdoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Provides {@link XeroAccount} platform connection.
   */
  providePlatformConnection = task.resolved(
    async (xeroAccount: XeroAccount) => {
      try {
        await this._options.onAction(xeroAccount)
        this.close()
      } catch (err: unknown) {
        const snackbar = this._newSnackbar()
        snackbar.fromError(err)
      }
    },
  )

  /**
   * Redirects the user to the Xero OAuth2 authorization page.
   */
  connectXero = task.resolved(() => {
    this._store.redirectToXeroOauth2Page(this._options.target.returnUrl())
  })

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    this.modal.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.modal.close()
  }
}
